const playbackStates = new WeakMap();

function loadVideo(video) {
    const sources = [video, ...video.querySelectorAll('source[data-src]')].filter((source) => source.dataset.src);
    if (!sources.length) return;

    sources.forEach((source) => {
        source.src = source.dataset.src;
        delete source.dataset.src;
    });
    video.load();
}

function updatePlayback(video, state) {
    if (!state.visible || document.hidden || !state.requested) {
        video.pause();
        return;
    }

    loadVideo(video);
    if (video.paused) video.play()?.catch(() => {});
}

// Components retain control over hover/focus playback without bypassing visibility.
export function playVideo(video) {
    const state = playbackStates.get(video);
    if (!state) return;
    state.requested = true;
    updatePlayback(video, state);
}

export function pauseVideo(video) {
    const state = playbackStates.get(video);
    if (state) state.requested = false;
    video.pause();
}

// Initialize per page/container and call the returned cleanup before removing it.
export function autoplayVideos(root = document) {
    const controller = new AbortController();
    const { signal } = controller;
    const videos = new Set();
    const loadObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            loadVideo(target);
            loadObserver.unobserve(target);
        });
    }, { rootMargin: '200px' });
    const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
            const state = playbackStates.get(target);
            state.visible = isIntersecting && intersectionRatio > 0;
            updatePlayback(target, state);
        });
    }, { threshold: 0.01 });

    const register = (scope) => {
        const candidates = scope.matches?.('video') ? [scope] : scope.querySelectorAll('video');
        candidates.forEach((video) => {
            if (playbackStates.has(video)) return;
            const state = { visible: false, requested: video.autoplay, autoplay: video.autoplay };
            playbackStates.set(video, state);
            videos.add(video);

            if (video.hasAttribute('muted')) {
                video.defaultMuted = true;
                video.muted = true;
            }

            // Keep autoplay intent in state; native autoplay can otherwise start offscreen.
            video.autoplay = false;
            video.pause();
            if (!video.hasAttribute('data-video-eager')) loadObserver.observe(video);
            visibilityObserver.observe(video);
        });
    };

    const refresh = () => videos.forEach((video) => updatePlayback(video, playbackStates.get(video)));
    document.addEventListener('visibilitychange', refresh, { signal });
    window.addEventListener('pageshow', refresh, { signal });
    // Mobile browsers may require a gesture; retry only requested, visible videos.
    document.addEventListener('click', refresh, { signal });
    document.addEventListener('infinite-scroll:loaded', (event) => {
        event.detail.elements.forEach((element) => {
            if (root.contains(element)) register(element);
        });
    }, { signal });
    register(root);

    return () => {
        controller.abort();
        loadObserver.disconnect();
        visibilityObserver.disconnect();
        videos.forEach((video) => {
            video.autoplay = playbackStates.get(video).autoplay;
            video.pause();
            playbackStates.delete(video);
        });
        videos.clear();
    };
}
